import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  lighten,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import {
  DepartmentSelect,
  UserSelect,
  AgeRangeSelect,
  ShiftPatternSelect,
  SexSelect,
} from "./Filters/components";

export interface FilterState {
  department: string[];
  searchUser: string[];
  ageRange: string[];
  sex: string[];
  shiftPattern: string[];
}

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  initialFilters?: Partial<FilterState>;
}

const DEFAULT_FILTERS: FilterState = {
  department: [],
  searchUser: [],
  ageRange: [],
  sex: [],
  shiftPattern: [],
};

export const getAppliedFilterCount = (
  filters?: Partial<FilterState> | null
): number => {
  if (!filters) return 0;

  const keys: Array<keyof FilterState> = [
    "department",
    "searchUser",
    "ageRange",
    "sex",
    "shiftPattern",
  ];

  return keys.reduce((count, key) => {
    const value = filters[key];
    return Array.isArray(value) && value.length > 0 ? count + 1 : count;
  }, 0);
};

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  initialFilters,
}) => {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  useEffect(() => {
    if (open) {
      setFilters({ ...DEFAULT_FILTERS, ...initialFilters });
    }
  }, [open, initialFilters]);

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          p: 2,
          backgroundColor: (theme) =>
            lighten(theme.palette.background.paper, 0.1),
        }}
      >
        <Box>
          <Typography variant="body1" gutterBottom>
            Filter Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please use following filters to refine results
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={3} sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 3 }}>
        <DepartmentSelect
          value={filters.department}
          onChange={(values) => setFilters({ ...filters, department: values })}
        />

        <UserSelect
          value={filters.searchUser}
          onChange={(userIds) =>
            setFilters({ ...filters, searchUser: userIds })
          }
        />

        <AgeRangeSelect
          value={filters.ageRange}
          onChange={(values) => setFilters({ ...filters, ageRange: values })}
        />

        <SexSelect
          value={filters.sex}
          onChange={(values) => setFilters({ ...filters, sex: values })}
        />

        <ShiftPatternSelect
          value={filters.shiftPattern}
          onChange={(values) =>
            setFilters({ ...filters, shiftPattern: values })
          }
        />
      </Stack>

      {/* Footer Actions */}
      <Box
        display="flex"
        gap={2}
        mt="auto"
        sx={{
          justifyContent: "flex-end",
          p: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: (theme) =>
            lighten(theme.palette.background.paper, 0.1),
        }}
      >
        <Button
          variant="outlined"
          startIcon={<RotateLeftIcon />}
          onClick={handleReset}
        >
          RESET
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={handleApply}
        >
          APPLY
        </Button>
      </Box>
    </Drawer>
  );
};
